import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Email,
  CheckCircle,
  Cancel,
  Warning,
  LocalShipping,
  SwapHoriz,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';

const ProductCancellationRefundPolicy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)',
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
        position: 'sticky',
        top: 0,
          zIndex: 100,
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
            <Typography variant="h5" fontWeight="600" color="white">
              Product Refund & Cancellation Policy
              </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold" color="#0A2A66" gutterBottom>
            NIStylez – Refund & Cancellation Policy
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
            At <strong>NIStylez</strong>, we want our customers to be completely satisfied with their purchases. This Refund & Cancellation Policy outlines the terms under which you may request a cancellation or refund.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Refund Policy */}
          <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CheckCircle sx={{ fontSize: 40, color: '#10b981', mr: 2 }} />
                <Typography variant="h4" fontWeight="bold" color="#1a237e">
                  1. Refund Policy
                      </Typography>
                      </Box>
              
              <Typography variant="h6" fontWeight="600" color="#1a237e" sx={{ mb: 2 }}>
                Eligibility for Refund:
              </Typography>
              
              <List sx={{ pl: 0, mb: 3 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Refunds are valid within 21 days from the date of receiving the product."
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Products must be in original condition, unused, unworn, unwashed, and free from damage or alterations."
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="All tags, labels, and original packaging must be intact and returned with the product."
                  />
                </ListItem>
              </List>

              <Typography variant="h6" fontWeight="600" color="#1a237e" sx={{ mb: 2 }}>
                Non-Refundable Items:
              </Typography>
              
              <List sx={{ pl: 0, mb: 3 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Cancel sx={{ color: '#ef4444', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Gift cards, promotional items, or discounted items marked 'Final Sale' are not eligible for refund."
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Cancel sx={{ color: '#ef4444', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Used, damaged, or tampered products are not eligible for return."
                  />
                </ListItem>
              </List>

              <Typography variant="h6" fontWeight="600" color="#1a237e" sx={{ mb: 2 }}>
                Refund Process:
              </Typography>
              
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Email sx={{ color: '#3b82f6', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="To request a refund, customers must contact our support team with order details."
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Once the product is received and inspected, an email confirmation will be sent regarding the approval or rejection of the refund."
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="If approved, the refund will be processed to the original payment method within 7–14 business days (processing times may vary depending on your bank/payment provider)."
                  />
                </ListItem>
            </List>
          </CardContent>
        </Card>

          {/* Cancellation Policy */}
          <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Cancel sx={{ fontSize: 40, color: '#f59e0b', mr: 2 }} />
                <Typography variant="h4" fontWeight="bold" color="#1a237e">
                  2. Cancellation Policy
            </Typography>
              </Box>
              
              <Typography variant="h6" fontWeight="600" color="#1a237e" sx={{ mb: 2 }}>
                Order Cancellation Window:
                  </Typography>
              
              <List sx={{ pl: 0, mb: 3 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />
                      </ListItemIcon>
                  <ListItemText
                    primary="Orders may be cancelled within 24 hours of placement with no penalty."
                  />
                    </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Email sx={{ color: '#3b82f6', fontSize: 24 }} />
                      </ListItemIcon>
                  <ListItemText
                    primary="To cancel, please contact us immediately."
                  />
                    </ListItem>
              </List>

              <Typography variant="h6" fontWeight="600" color="#1a237e" sx={{ mb: 2 }}>
                After 24 Hours:
              </Typography>
              
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
                      </ListItemIcon>
                  <ListItemText
                    primary="Once an order has been processed or shipped, it can no longer be cancelled. Customers may still request a refund (if eligible) after receiving the product."
                  />
                    </ListItem>
                  </List>
            </CardContent>
          </Card>

          {/* Return Shipping */}
          <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocalShipping sx={{ fontSize: 40, color: '#6366f1', mr: 2 }} />
                <Typography variant="h4" fontWeight="bold" color="#1a237e">
                  3. Return Shipping
                </Typography>
                </Box>
              
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
                      </ListItemIcon>
                  <ListItemText
                    primary="Customers are responsible for return shipping costs unless the return is due to an error on our part (e.g., wrong or defective item)."
                  />
                    </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocalShipping sx={{ color: '#6366f1', fontSize: 24 }} />
                      </ListItemIcon>
                  <ListItemText
                    primary="We recommend using a trackable shipping service and keeping proof of postage. NIStylez is not responsible for lost or undelivered return shipments."
                  />
                    </ListItem>
                  </List>
          </CardContent>
        </Card>

          {/* Exchanges */}
          <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SwapHoriz sx={{ fontSize: 40, color: '#8b5cf6', mr: 2 }} />
                <Typography variant="h4" fontWeight="bold" color="#1a237e">
                  4. Exchanges
                  </Typography>
                </Box>
              
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Direct exchanges are currently not supported. If you wish to replace a product, please request a refund (if eligible) and place a new order.
                  </Typography>
          </CardContent>
        </Card>

          <Divider sx={{ my: 4 }} />

        {/* Contact Information */}
          <Card sx={{ borderRadius: 3, overflow: 'hidden', background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Email sx={{ fontSize: 60, color: 'white', mb: 3 }} />
              <Typography variant="h4" fontWeight="bold" color="white" gutterBottom>
                Contact Us
            </Typography>
              <Typography variant="h6" color="white" sx={{ opacity: 0.9, mb: 3 }}>
                For any refund, cancellation, or product-related queries, please contact our customer service team:
            </Typography>
              <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2, 
                p: 3, 
                display: 'inline-block',
                backdropFilter: 'blur(10px)'
              }}>
                <Typography variant="h5" fontWeight="bold" color="white">
                  📧 nistylezaustralia@gmail.com
            </Typography>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Container>

        {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default ProductCancellationRefundPolicy;
